import { Server, Code2, Gift, ShieldCheck, Heart, Sparkles, Quote, Bot, HardDrive, Bell } from "lucide-react";
import { GoldCheck } from "@/components/GoldCheck";
import { IdeaVote } from "@/components/IdeaVote";
import { MugWall } from "@/components/merch/MugWall";
import { UnboxingTeaser } from "@/components/UnboxingGallery";
import { SupportBank, SupportClip, SupportPerks, SupportSteps } from "@/components/csupport/ProjectSections";

const COSTS = [
  [Server, "Server & Datenbank", "Die App läuft rund um die Uhr auf Servern – jedes Login, jeder Beitrag, jede Punktebuchung."],
  [HardDrive, "Speicher für Fotos & Videos", "Profilbilder, Titelbilder, Beiträge und Storys brauchen sicheren, schnellen Speicherplatz."],
  [Bell, "Push-Nachrichten & E-Mails", "Jede Benachrichtigung aufs Handy und jede E-Mail (Willkommen, Passwort, Geschenke) kostet Gebühren."],
  [Bot, "KI-Assistent Itzi", "Jede Frage an Itzi wird von einer hochwertigen KI beantwortet – das wird pro Antwort berechnet."],
  [Code2, "Entwicklung neuer Funktionen", "Glücksrad, Rubbellose, Duelle, Chat: Jede Funktion bedeutet viele Stunden Planung, Entwicklung und Tests."],
  [Gift, "Geschenke für die Community", "Die Geschenke, die ihr gegen Punkte eintauscht, kaufen und verschicken wir selbst."],
  [ShieldCheck, "Sicherheit & Moderation", "Datenschutz, Schutz vor Missbrauch und das Prüfen von Meldungen."],
  [Sparkles, "Keine Werbung, kein Datenverkauf", "Wir finanzieren die Community nicht über Werbung oder eure Daten – deshalb zählt jede Unterstützung."],
];

export default function Support() {
  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="support-page">
      <div className="relative overflow-hidden rounded-[2rem] vip-lux p-6 sm:p-10 shadow-2xl shadow-[#9f1239]/30 border border-[#fda4af]/40 fade-up" data-testid="support-hero">
        <span className="vip-lux-pattern" aria-hidden />
        <span className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#fda4af]/20 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <span className="w-14 h-14 rounded-2xl vip-gold-metal flex items-center justify-center"><Heart className="w-7 h-7" fill="currentColor" /></span>
          <GoldCheck className="w-9 h-9" />
        </div>
        <p className="relative text-xs font-extrabold uppercase tracking-[0.3em] text-[#fecdd3] mt-6">Projekt Unterstützer werden</p>
        <h1 className="relative font-display text-3xl sm:text-5xl font-extrabold leading-[1.05] mt-2 vip-gold-title">Trag die Community mit – als Projekt Unterstützer.</h1>
        <p className="relative mt-4 text-[#ffe4e6] leading-relaxed text-base sm:text-lg">Projekt Unterstützer sind Mitglieder, die itzPlay <b className="text-white">finanziell unterstützen</b> und so helfen, die sehr hohen laufenden Kosten der Plattform zu tragen. Nur dadurch bleibt die Community für alle kostenlos – und kann weiter wachsen.</p>
        <a href="#vip-konto" data-testid="support-hero-cta" className="relative mt-6 inline-flex h-12 px-6 items-center gap-2 rounded-full vip-gold-metal font-extrabold active:scale-95 transition-transform"><Heart className="w-4 h-4" fill="currentColor" /> Jetzt Projekt Unterstützer werden</a>
      </div>

      <UnboxingTeaser />
      <SupportClip />

      <figure className="relative rounded-[2rem] bg-white border border-pink-100 p-6 sm:p-8 shadow-sm fade-up" data-testid="support-message">
        <Quote className="absolute top-5 right-6 w-10 h-10 text-rose-100" />
        <p className="text-xs font-bold uppercase tracking-widest text-rose-500">Eine Botschaft von uns</p>
        <blockquote className="mt-3 font-display text-2xl sm:text-3xl font-extrabold leading-snug text-slate-900">„Ohne euch gäbe es diese Community nicht. Mit eurer Hilfe machen wir sie zu etwas ganz Großem.“</blockquote>
        <figcaption className="mt-4 flex items-center gap-2 text-sm font-semibold text-slate-500"><Heart className="w-4 h-4 text-rose-500 fill-rose-500" /> Saskia & Stefan</figcaption>
      </figure>

      <section className="space-y-3" data-testid="support-costs">
        <div>
          <p className="text-xs font-extrabold tracking-[0.3em] uppercase text-rose-500">Ehrlich & transparent</p>
          <h2 className="font-display text-2xl font-extrabold mt-1">Was die Plattform jeden Monat kostet</h2>
          <p className="text-sm text-slate-500 mt-1">Eine App wie itzPlay verursacht hohe laufende Kosten – Monat für Monat. Diese Faktoren kosten Geld:</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {COSTS.map(([Icon, t, x], i) => (
            <div key={t} className="bg-white rounded-2xl p-5 border border-pink-100 fade-up" style={{ animationDelay: `${i * 50}ms` }} data-testid={`support-cost-${i}`}>
              <span className="w-10 h-10 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></span>
              <p className="font-bold">{t}</p>
              <p className="text-sm text-slate-500 mt-1 leading-relaxed">{x}</p>
            </div>
          ))}
        </div>
      </section>

      <SupportPerks />
      <SupportSteps />
      <SupportBank />

      <MugWall />

      <IdeaVote />
      <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1.5"><Heart className="w-3.5 h-3.5 text-rose-400 fill-rose-400" /> Die Unterstützung ist freiwillig. Alle Grundfunktionen und Punkte bleiben für alle kostenlos – Projekt Unterstützer erhalten als Dankeschön exklusive Extras.</p>
    </div>
  );
}
